import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineProfile } from '@/app/components/icons/BaseLineProfile';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/app/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { EN_FAQ_URL, isEnterpriseDistribution, SITE, ZH_FAQ_URL } from '@/app/constants';

import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { openOrRedirect, safeStorage } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { UserAvatar } from '../UserAvatar';
import bg from './bg.svg';
import { MobileUserNameDialog } from './MobileUserNameDialog';
import { useSignOut } from './use-sign-out';

export const MobileUserMenu = () => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [showInfo, setShowInfo] = useState(false);

  const [showError, setShowError] = useState(false);
  const [openMobileUserNameDialog, setOpenMobileUserNameDialog] = useState(false);
  const { userInfo, creditsInfo, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    creditsInfo: state.creditsInfo,
    isTeamSpace: state.isTeamSpace,
  }));

  const signOut = useSignOut();
  const { open: openCreditDetailsDialog } = useGlobalModal('creditDetailsDialog');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openFreeCreditsTaskDialog } = useGlobalModal('freeCreditsTaskDialog');

  const chinaAgreementMenuItems = [
    {
      key: 'community',
      label: '平台自律公约',
      href: '/terms/community',
    },
    {
      key: 'privacy',
      label: '隐私政策',
      href: '/privacy',
    },
    {
      key: 'privacy-summary',
      label: t('home3.footer.privacySummary'),
      href: '/privacy-summary',
    },
    {
      key: 'personal-info-collection',
      label: t('home3.footer.personalInfoCollection'),
      href: '/personal-info-collection',
    },
    {
      key: 'personal-info-sharing',
      label: t('home3.footer.personalInfoSharing'),
      href: '/personal-info-sharing',
    },
    {
      key: 'terms',
      label: '用户协议',
      href: '/terms',
    },
  ];

  const handleOpenFreeCreditsTaskDialog = () => {
    openFreeCreditsTaskDialog({
      downloadAppDialogProps: {
        fromPage: 'mobile_h5',
      },
      onClose: (type) => {
        if (type === 'open-my-subscription-page') {
          setShowInfo(false);
        }
      },
      creditDetailsDialogProps: {
        onClose: (type) => {
          if (type === 'open-my-subscription-page') {
            setShowInfo(false);
          }
        },
      },
    });
  };

  const handelChangeNickname = () => {
    if (userInfo?.auditing_info?.nickname) {
      toast({
        title: t('personal.bio.auditing'),
      });
      return;
    }

    setOpenMobileUserNameDialog(true);
  };

  if (!userInfo) return null;

  return (
    <div className="hidden size-9 select-none md:block">
      <span
        className="relative size-full"
        onClick={() => {
          setShowInfo(true);
        }}
      >
        <UserAvatar userInfo={userInfo} />
      </span>
      {showInfo && (
        <div className="fixed top-0 left-0 flex h-screen w-full flex-col justify-start gap-4 overflow-y-auto bg-black p-4 sm:pb-[var(--ios-safe-bottom)]">
          <div
            className="absolute top-4 right-4 flex size-9 items-center justify-center rounded-full bg-system-white12"
            onClick={() => {
              setShowInfo(false);
            }}
          >
            <BaseLineClose className="text-base text-white" />
          </div>
          <div className="relative mt-20 rounded-8 border border-system-white12 bg-[linear-gradient(276deg,#1C2636_32.19%,#29353A_95.94%)] p-4 text-system-white">
            <img src={bg.src} alt="Background" className="absolute top-4 right-4 h-98" />
            <div className="mb-4 flex items-center gap-3">
              <UserAvatar userInfo={userInfo} className="size-12" editable={true} />
              <span>
                <div className="flex items-center gap-2">
                  <div className="max-w-[240px] truncate font-medium text-base text-system-white leading-6">
                    {userInfo.nickname}
                  </div>
                  <BaseLineEdit className="z-10 size-4" onClick={() => handelChangeNickname()} />
                </div>
                <div className="font-light text-sm text-system-white64 leading-6">
                  {SITE === 'china' ? userInfo.phone : userInfo.email}
                </div>
              </span>
            </div>
            <div className="mb-3 flex items-center">
              <span className="mr-4 bg-[linear-gradient(90deg,#5AE0FF,#009BFF)] bg-clip-text font-medium text-[40px] text-transparent leading-[56px]">
                {creditsInfo?.total ?? 0}
              </span>
              <span className="text-sm text-system-white64">{t('payments.current.plan')}</span>
              <span className="mx-2 h-[10px] w-[1px] bg-[rgba(217,217,217,0.2)]" />
              <span className="font-bold text-white">
                {t(`payments.${(userInfo.subs_plan ?? 'free') as SubsPlanType}`)}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-10 py-1">
              <div className="flex flex-col items-start justify-start">
                <span className="font-medium text-2xl text-system-white leading-snug">{creditsInfo?.free ?? 0}</span>
                <span className={cn('text-wrap text-system-white64 text-xs')}>{t('payments.current.free')}</span>
              </div>
              <div className="flex flex-col items-start justify-start">
                <span className="font-medium text-2xl text-system-white leading-snug">
                  {creditsInfo?.subscribed ?? 0}
                </span>
                <span className="text-wrap text-system-white64 text-xs">{t('payments.current.subscribed')}</span>
              </div>
              <div className="flex flex-col items-start justify-start">
                <span className="font-medium text-2xl text-system-white leading-snug">
                  {creditsInfo?.purchased ?? 0}
                </span>
                <span className="text-wrap text-system-white64 text-xs">{t('payments.current.purchased')}</span>
              </div>
            </div>
          </div>
          <div className="text-sm text-system-white">
            {!isTeamSpace && (
              <div
                className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm leading-6"
                onClick={() => {
                  router.push(`/profile/${userInfo.id}`);
                  setShowInfo(false);
                }}
              >
                <div className="flex items-center gap-2">
                  <BaseLineProfile className="text-base" />
                  <span>{t('sidebar.profile')}</span>
                </div>
                <BaseLineRight className="ms-1" />
              </div>
            )}
            {!isEnterpriseDistribution && (
              <div
                className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm leading-6"
                onClick={() => {
                  handleOpenFreeCreditsTaskDialog();
                  reportEvent('taskcenter_exposure', {
                    refer_element: 'MobileUserMenu',
                    refer_page: pathname,
                  });
                }}
              >
                {t('freeCreditsDialog.title')}
                <BaseLineRight className="ms-1" />
              </div>
            )}
            <div
              className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm leading-6"
              onClick={() => {
                openCreditDetailsDialog({
                  onClose: (type) => {
                    if (type === 'open-my-subscription-page') {
                      setShowInfo(false);
                    }
                  },
                });
              }}
            >
              {t('payments.current.details')}
              <BaseLineRight className="ms-1" />
            </div>
            {!isEnterpriseDistribution && (
              <>
                <div
                  onClick={() => openOrRedirect(locale === 'zh' ? ZH_FAQ_URL : EN_FAQ_URL)}
                  className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm leading-6"
                >
                  {t('personal.help')} <BaseLineRight className="ms-1" />
                </div>
                {SITE === 'china' && (
                  <Accordion type="single" collapsible>
                    <AccordionItem value="agreement" className="border-none">
                      <AccordionTrigger className="px-4 py-3 font-normal text-sm leading-6 hover:no-underline">
                        平台协议
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        {chinaAgreementMenuItems.map((item) => (
                          <div
                            key={item.key}
                            onClick={() => openOrRedirect(item.href)}
                            className="flex cursor-pointer items-center justify-between px-8 py-3 text-sm text-system-white64 leading-6"
                          >
                            {item.label} <BaseLineRight className="ms-1" />
                          </div>
                        ))}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
                {SITE === 'global' && (
                  <div
                    onClick={() => openOrRedirect('/cookies')}
                    className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm leading-6"
                  >
                    {t('home3.footer.cookiesPolicy')} <BaseLineRight className="ms-1" />
                  </div>
                )}
              </>
            )}
          </div>

          {!isEnterpriseDistribution && (
            <>
              <div
                className="flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-8 bg-system-bg09 py-2 font-medium text-sm text-white"
                onClick={() => {
                  openPlanDetailDialog({ fromPage: 'header credit balance card button' });
                  reportEvent('vip_price_modal_exposure', {
                    from_page: 'header credit balance card button',
                  });
                }}
              >
                {t('payments.current.subscribeDetails')}
              </div>
              <div
                className="flex h-9 cursor-pointer items-center justify-center rounded-8 bg-system-bg09 py-2 font-medium text-sm text-white aria-disabled:opacity-60"
                aria-disabled={userInfo.subs_plan === 'free'}
                onClick={() => {
                  if (userInfo.subs_plan === 'free') {
                    setShowError(true);
                    return;
                  }
                  openPurchaseCreditsDialog({
                    showTriggerButton: false,
                  });
                }}
              >
                {t('payments.current.purchaseCredits')}
              </div>
            </>
          )}

          <div className="flex flex-1 flex-col justify-end">
            <div
              className="flex h-12 w-full items-center justify-center rounded-8 px-2 py-3 text-system-white64"
              onClick={() => {
                safeStorage.removeItem('utm_term');
                signOut();
              }}
            >
              {t('personal.logout')}
            </div>
          </div>
        </div>
      )}
      <Dialog open={showError}>
        <DialogContent closable={false}>
          <DialogHeader>
            <DialogTitle className="text-center">{t('payments.current.purchaseCredits')}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-system-white64 leading-5">
            {t('payments.current.purchaseCreditsDisabledTip')}
          </div>
          <div
            className="flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-8 bg-ShengshuButton py-2 font-medium text-black text-sm"
            onClick={() => {
              setShowError(false);
            }}
          >
            {t('popup.dialog.notifications.ok')}
          </div>
        </DialogContent>
      </Dialog>

      <MobileUserNameDialog open={openMobileUserNameDialog} setOpen={setOpenMobileUserNameDialog} />
    </div>
  );
};
